import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Badge, Col, Form, Row } from "react-bootstrap";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import type { ContentFileDto, UpdateFileDto } from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { toFormValues, useForm } from "../common/form/useForm";
import { Icon } from "../common/Icon";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { LinkGen, QueryKeys } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { markCanvaEditorOpened } from "../media/canvaEditorSession";
import { useCanvaQuery } from "../settings/useIntegrations";
import "./PageContentEdit.css";
import { useMediaActions } from "./useMediaActions";
import { useSuspenseMediaItem } from "./useMediaBatch";
import { useMediaFolders, useMoveFiles } from "./useMediaFolders";

function getPath(file: ContentFileDto) {
	if (Object.hasOwn(file.thumbnails, "full")) {
		return file.thumbnails.full;
	}

	return file.url;
}

export const PageMediaEdit = () => {
	const { id: stringID } = useParams();
	const ID = stringID ? Number(stringID) : null;
	const isIdInteger = Number.isInteger(ID);
	const file = useSuspenseMediaItem(isIdInteger ? ID : null);
	const { update, remove, invalidateMedia } = useMediaActions();
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();
	const isCanvaEnabled = useFeatureFlag("CanvaIntegration");
	const { data: canvaStatus } = useCanvaQuery();
	const { data: folders = [] } = useMediaFolders();
	const moveFiles = useMoveFiles();
	const [currentFolderId, setCurrentFolderId] = useState<number | null>(
		file?.folderId ?? null,
	);
	const [canvaActionLoading, setCanvaActionLoading] = useState<
		"edit" | "update" | null
	>(null);

	const navigate = useNavigate();
	const initial = useMemo(
		() =>
			file
				? (toFormValues(file) satisfies UpdateFileDto)
				: ({ description: "", filename: "" } satisfies UpdateFileDto),
		[file],
	);

	const form = useForm({
		initialValues: initial,
		onSubmit: async (values) => {
			if (file) {
				await update.mutateAsync({ id: file.id, dto: values });
				navigate(LinkGen.Media());
			}
		},
	});

	const deleteFile = async () => {
		if (file) {
			await remove.mutateAsync(file.id);
			navigate(LinkGen.Media());
		}
	};

	// Sync folder state when file loads
	useEffect(() => {
		if (file) {
			setCurrentFolderId(file.folderId);
		}
	}, [file]);

	const handleFolderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const val = e.target.value;
		const newFolderId = val === "" ? null : Number(val);
		if (file) {
			setCurrentFolderId(newFolderId);
			moveFiles.mutate({ fileIds: [file.id], folderId: newFolderId });
		}
	};

	const canUseCanvaActions =
		isCanvaEnabled &&
		canvaStatus?.connected &&
		file?.source === "canva" &&
		file.externalId;

	const handleEditInCanva = async () => {
		if (!file?.externalId) return;
		setCanvaActionLoading("edit");
		try {
			const res = await Api.CanvaGetEditUrl(file.externalId, file.id);
			markCanvaEditorOpened();
			window.open(res.data.editUrl, "_blank", "noopener,noreferrer");
		} catch {
			emit({
				type: AlertType.ERROR,
				content:
					"Could not open Canva. Try reconnecting Canva in Settings → Apps.",
			});
		} finally {
			setCanvaActionLoading(null);
		}
	};

	const handleUpdateFromCanva = async () => {
		if (!file) return;
		setCanvaActionLoading("update");
		try {
			const res = await Api.CanvaReimportContentFile(file.id);
			const importId = res.data.id;
			const deadline = Date.now() + 90_000;
			let finalStatus = res.data;

			while (
				finalStatus.status !== "completed" &&
				finalStatus.status !== "failed" &&
				Date.now() < deadline
			) {
				await new Promise((resolve) => setTimeout(resolve, 2000));
				finalStatus = (await Api.CanvaImportStatus(importId)).data;
			}

			if (finalStatus.status === "failed" || finalStatus.succeeded === 0) {
				const item = finalStatus.items[0];
				emit({
					type: AlertType.ERROR,
					content:
						item?.errorMessage ??
						"Update from Canva failed. Check API logs for details.",
				});
				return;
			}

			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Files.Single(companyId, file.id),
			});
			await invalidateMedia();
			emit({
				type: AlertType.SUCCESS,
				content: "Updated from Canva.",
			});
		} catch {
			emit({
				type: AlertType.ERROR,
				content: "Failed to update from Canva. Please try again.",
			});
		} finally {
			setCanvaActionLoading(null);
		}
	};

	// if file not found, go back to content page
	useEffect(() => {
		if (!file) {
			navigate(LinkGen.Media());
		}
	}, [file, navigate]);

	if (!file) {
		return null;
	}

	return (
		<AppPage title={"Edit Media"}>
			<div className="mb-3 d-flex flex-row justify-items-center align-items-center">
				<Icon name="arrow_back_ios" />
				<NavLink to={LinkGen.Media()}>Back to media library</NavLink>
			</div>
			<Form className="form-file" onSubmit={form.handleSubmit}>
				<Row>
					<Col md={6}>
						<Card>
							<Card.Header>Content Information</Card.Header>
							<Card.Body>
								<ExtraForm.Group label="Filename" className="mb-3">
									<div className="field-extension">
										<ExtraForm.Control
											{...form.getFieldProps("filename")}
											{...form.getFieldState("filename")}
										/>
										<span>.{file?.extension}</span>
									</div>
								</ExtraForm.Group>
								<ExtraForm.Group label="Description" className="mb-3">
									<ExtraForm.Control
										as="textarea"
										rows={4}
										{...form.getFieldProps("description")}
										{...form.getFieldState("description")}
										placeholder="Describe what is on the picture"
									/>
								</ExtraForm.Group>
								<ExtraForm.Group label="Folder" className="mb-3">
									<ExtraForm.Select
										value={currentFolderId ?? ""}
										onChange={handleFolderChange}
									>
										<option value="">Unfiled</option>
										{folders
											.filter((f) => !f.isArchive)
											.map((f) => (
												<option key={f.id} value={f.id}>
													{f.name}
												</option>
											))}
										{folders
											.filter((f) => f.isArchive)
											.map((f) => (
												<option key={f.id} value={f.id}>
													{f.name}
												</option>
											))}
									</ExtraForm.Select>
								</ExtraForm.Group>
								{file.source === "heygen" && (
									<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
										<Badge bg="light" text="dark" className="border">
											From HeyGen
										</Badge>
										{file.externalUrl && (
											<a
												href={file.externalUrl}
												target="_blank"
												rel="noopener noreferrer"
											>
												Open in HeyGen
											</a>
										)}
										{file.subtitlesUrl && (
											<a href={file.subtitlesUrl} download>
												Download subtitles
											</a>
										)}
									</div>
								)}
								{canUseCanvaActions && (
									<div className="d-flex flex-wrap gap-2 mb-3">
										<Button
											variant="secondary"
											loading={canvaActionLoading === "edit"}
											onClick={handleEditInCanva}
										>
											Edit in Canva
										</Button>
										<Button
											variant="secondary"
											loading={canvaActionLoading === "update"}
											onClick={handleUpdateFromCanva}
										>
											Update from Canva
										</Button>
									</div>
								)}
								<div className={"d-flex justify-content-between"}>
									<Button icon="save" loading={update.isPending} type="submit">
										Save
									</Button>
									<Button
										icon="delete"
										loading={remove.isPending}
										variant="danger"
										onClick={deleteFile}
									>
										Delete
									</Button>
								</div>
							</Card.Body>
						</Card>
					</Col>
					<Col md={6}>
						<Card>
							<Card.Header>Preview</Card.Header>
							<Card.Body>
								<div className="content-edit-preview">
									{file?.type === "image" ? (
										<img
											key={file.importedAt ?? file.updatedAt}
											src={getPath(file)}
											alt={file?.filename}
										/>
									) : (
										<video
											key={file.importedAt ?? file.updatedAt}
											muted
											controls
										>
											<source
												src={`${file.url}?v=${encodeURIComponent(
													file.importedAt ?? file.updatedAt,
												)}`}
												type="video/mp4"
											/>
										</video>
									)}
								</div>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</Form>
		</AppPage>
	);
};
